import {useCallback, useMemo, useState} from 'react'
import {useSessionEvents} from './use-session-events'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'

export interface UseEventsPromptStateOptions {
  /** The task ID to fetch events for */
  taskId: string | undefined
  /** Whether to enable the hook */
  enabled: boolean
}

export interface EventsPromptState {
  /** The current pending prompt, or null if none */
  pendingPrompt: NormalizedPrompt | null
  /** Whether events are still loading */
  isLoading: boolean
  /** Not applicable for events-based prompts (always false) */
  isExpired: boolean
  /** Mark the current prompt as resolved client-side (prevents resurrection before server confirms) */
  markPromptResolved: (toolCallId: string) => void
  /** Roll back an optimistic resolution so the prompt re-appears on mutation failure */
  unmarkPromptResolved: (toolCallId: string) => void
  /** Dismiss the current prompt by marking it resolved client-side */
  dismissPrompt: () => void
}

/**
 * Detects the earliest unresolved interactive prompt from the session's
 * conversation model and provides callbacks for optimistic resolution.
 */
export function useEventsPromptState({taskId, enabled}: UseEventsPromptStateOptions): EventsPromptState {
  const [resolvedPromptIds, setResolvedPromptIds] = useState<Set<string>>(() => new Set())

  const {data, isLoading} = useSessionEvents({
    taskId,
    enabled: enabled && !!taskId,
  })

  const model = data?.model
  const allUnresolvedPrompts = model?.allUnresolvedPrompts
  const serverResolvedIds = model?.serverResolvedIds

  // Find the first unresolved prompt not already marked resolved client-side
  const pendingPrompt = useMemo(() => {
    if (!allUnresolvedPrompts?.length) return null
    for (const prompt of allUnresolvedPrompts) {
      if (resolvedPromptIds.has(prompt.toolCallId)) continue
      if (serverResolvedIds?.has(prompt.toolCallId)) continue
      return prompt
    }
    return null
  }, [allUnresolvedPrompts, serverResolvedIds, resolvedPromptIds])

  const markPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => new Set(prev).add(toolCallId))
  }, [])

  const unmarkPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => {
      if (!prev.has(toolCallId)) return prev
      const next = new Set(prev)
      next.delete(toolCallId)
      return next
    })
  }, [])

  const dismissPrompt = useCallback(() => {
    if (pendingPrompt) {
      setResolvedPromptIds(prev => new Set(prev).add(pendingPrompt.toolCallId))
    }
  }, [pendingPrompt])

  return {
    pendingPrompt,
    isLoading,
    isExpired: false,
    markPromptResolved,
    unmarkPromptResolved,
    dismissPrompt,
  }
}
